import React, { useEffect, useRef, useState } from "react";
import { Save, X } from "lucide-react";

// A themed "Save Preset" dialog that replaces the previous window.prompt()
// flow. Matches the app's global AlertModal chrose (accent bar header,
// rounded card, footer button pair) but adds the actual form fields the
// save flow needs: name, optional description, scope, and default toggle.
export const SavePresetModal = ({
  isOpen,
  onClose,
  onConfirm, // async ({ name, description, scope, isDefault }) => void — throws on failure
  canShareCompany = false,
  currentTheme,
  theme = "light",
  isRTL = false,
  t = (k) => k,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scope, setScope] = useState("PERSONAL");
  const [isDefault, setIsDefault] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setDescription("");
      setScope("PERSONAL");
      setIsDefault(false);
      setIsSaving(false);
      setError("");
      const timer = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const accent = currentTheme.colors.primary;
  const bg = theme === "dark" ? "#0b1220" : "#ffffff";
  const cardBg = theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)";
  const border = theme === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)";

  const trimmedName = name.trim();

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!trimmedName) {
      setError(t("presetNameRequired") || "Please enter a name for this preset.");
      inputRef.current?.focus();
      return;
    }
    setIsSaving(true);
    setError("");
    try {
      await onConfirm({
        name: trimmedName,
        description: description.trim() || undefined,
        scope,
        isDefault,
      });
      // Parent closes the modal on success.
    } catch (err) {
      setIsSaving(false);
      setError(err?.message || t("presetSaveFailed") || "Failed to save preset. Please try again.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      if (!isSaving) onClose();
    }
  };

  return (
    <div
      onClick={() => !isSaving && onClose()}
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(2,6,23,0.6)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(440px, 92%)",
          borderRadius: 12,
          background: bg,
          color: currentTheme.colors.textPrimary,
          border,
          boxShadow: "0 8px 30px rgba(2,6,23,0.6)",
          overflow: "hidden",
        }}
      >
        {/* Header — same accent-bar chrome as the global AlertModal */}
        <div
          style={{
            padding: "18px 20px",
            borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ width: 10, height: 40, background: accent, borderRadius: 4 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {t("savePresetAs") || "Save preset as…"}
              </div>
              <div style={{ fontSize: "0.78rem", color: currentTheme.colors.textSecondary, marginTop: 2 }}>
                {t("savePresetSubtitle") || "Give this column selection a name so you can reuse it later."}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !isSaving && onClose()}
            style={{
              background: "transparent",
              border: "none",
              color: currentTheme.colors.textMuted,
              cursor: isSaving ? "default" : "pointer",
              padding: 4,
              display: "flex",
            }}
            disabled={isSaving}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: currentTheme.colors.textSecondary,
                  marginBottom: "6px",
                }}
              >
                {t("presetName") || "Preset name"} <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                ref={inputRef}
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (error) setError("");
                }}
                placeholder={t("presetNamePlaceholder") || "e.g. Monthly A/D Export"}
                maxLength={80}
                disabled={isSaving}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 8,
                  border: error
                    ? "1px solid #ef4444"
                    : `1px solid ${currentTheme.colors.borderLight}`,
                  background: cardBg,
                  color: currentTheme.colors.textPrimary,
                  fontSize: "0.88rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: currentTheme.colors.textSecondary,
                  marginBottom: "6px",
                }}
              >
                {t("presetDescription") || "Description"}{" "}
                <span style={{ fontWeight: 400, color: currentTheme.colors.textMuted }}>
                  ({t("optional") || "optional"})
                </span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("presetDescriptionPlaceholder") || "What is this preset for?"}
                maxLength={200}
                rows={2}
                disabled={isSaving}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 8,
                  border: `1px solid ${currentTheme.colors.borderLight}`,
                  background: cardBg,
                  color: currentTheme.colors.textPrimary,
                  fontSize: "0.85rem",
                  outline: "none",
                  resize: "vertical",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {canShareCompany && (
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: currentTheme.colors.textSecondary,
                    marginBottom: "6px",
                  }}
                >
                  {t("presetVisibility") || "Visibility"}
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  {[
                    { key: "PERSONAL", label: t("presetPersonal") || "Only me" },
                    { key: "COMPANY", label: t("presetCompany") || "Whole company" },
                  ].map((opt) => {
                    const active = scope === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setScope(opt.key)}
                        disabled={isSaving}
                        style={{
                          flex: 1,
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: active
                            ? `1px solid ${accent}`
                            : `1px solid ${currentTheme.colors.borderLight}`,
                          background: active ? `${accent}18` : "transparent",
                          color: active ? accent : currentTheme.colors.textMuted,
                          fontSize: "0.8rem",
                          fontWeight: active ? 600 : 400,
                          cursor: isSaving ? "default" : "pointer",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "0.82rem",
                color: currentTheme.colors.textSecondary,
                cursor: isSaving ? "default" : "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                disabled={isSaving}
                style={{ width: 15, height: 15, cursor: isSaving ? "default" : "pointer" }}
              />
              {t("presetSetAsDefault") || "Open this preset by default next time"}
            </label>

            {error && (
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#ef4444",
                  background: theme === "dark" ? "rgba(239,68,68,0.1)" : "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 8,
                  padding: "8px 10px",
                }}
              >
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              padding: "12px 16px",
              borderTop: `1px solid ${currentTheme.colors.borderLight}`,
            }}
          >
            <button
              type="button"
              onClick={() => !isSaving && onClose()}
              disabled={isSaving}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: `1px solid ${currentTheme.colors.borderLight}`,
                background: theme === "dark" ? "#111827" : "#fff",
                color: currentTheme.colors.textPrimary,
                fontSize: "0.85rem",
                cursor: isSaving ? "default" : "pointer",
              }}
            >
              {t("cancel") || "Cancel"}
            </button>
            <button
              type="submit"
              disabled={isSaving || !trimmedName}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: !trimmedName ? `${accent}80` : accent,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: isSaving || !trimmedName ? "default" : "pointer",
                opacity: isSaving ? 0.85 : 1,
              }}
            >
              <Save size={14} />
              {isSaving ? (t("saving") || "Saving…") : (t("savePreset") || "Save preset")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
